import { BackendError, Service } from '@genial/services';
import {
  CoeOfferDetailEntity,
  CoeOfferEntity,
  CoeOfferStrategyDirectionEntity,
  MetaResponse,
  ResponsesCoeMarketStatusResponse,
} from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { getOfferByDetailProductIdService } from '../../../pages/BatchTicket/services';
import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  structuredOperationsOffersParamsState,
  structuredOperationsOffersAllState,
  structuredOperationsBySearchTermState,
  structuredOperationsOffersBySearchTermParamsState,
  productItemState,
  structuredOperationsOptionBillingTypeSelectedState,
  structuredOperationsDetailsState,
  structuredOperationProductSelectedState,
  structuredOperationsOptionsFilterState,
  structuredOperationsMarketStatusState,
} from '../atom';
import { screenName } from '../constants';
import {
  normalizeCoesFilters,
  normalizeCoesFiltersFromMeta,
  normalizeCoesOffers,
} from '../normalizer/coes';
import {
  CoesOfferEntityNormalized,
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
  DynamicMarketStatusProps,
  StructuredOperationBillingTypeSelected,
  StructuredOperationsOffersBySearchTerm,
  StructuredOperationsOffersParams,
} from '../types';

type StructuredOperationsQueryData = {
  data: CoeOfferEntity[];
  meta: MetaResponse;
};

export const structuredOperationsOffersParams = selector({
  key: 'structuredOperationsOffersParams',
  set: ({ set, get }, newValue) => {
    const currentStructuredOperationsParamsOffers = get(structuredOperationsOffersParamsState);
    const value = newValue as Partial<StructuredOperationsOffersParams>;
    const structuredOperationsOffers = { ...currentStructuredOperationsParamsOffers, ...value };

    return set(structuredOperationsOffersParamsState, structuredOperationsOffers);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsOffersParamsState);
    return item;
  },
});

export const structuredOperationsOffersBySearchTermParams = selector({
  key: 'structuredOperationsOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentStructuredOperationsParamsOffersBySearchTerm = get(
      structuredOperationsOffersBySearchTermParamsState
    );
    const value = newValue as Partial<StructuredOperationsOffersBySearchTerm>;
    const structuredOperationsOffersBySearchTerm = {
      ...currentStructuredOperationsParamsOffersBySearchTerm,
      ...value,
    };

    return set(
      structuredOperationsOffersBySearchTermParamsState,
      structuredOperationsOffersBySearchTerm
    );
  },
  get: ({ get }) => {
    const item = get(structuredOperationsOffersBySearchTermParamsState);
    return item;
  },
});

export const updateStructuredOperationsOffersAllSelector = selector({
  key: 'updateStructuredOperationsOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CoeOfferEntity[];
    return set(structuredOperationsOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsOffersAllState);
    return item;
  },
});

export const updateStructuredOperationsBillingTypeSelectedSelector = selector({
  key: 'updateStructuredOperationsBillingTypeSelectedSelector',
  set: ({ set }, newValue) => {
    const data = newValue as StructuredOperationBillingTypeSelected;
    return set(structuredOperationsOptionBillingTypeSelectedState, data);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsOptionBillingTypeSelectedState);
    return item;
  },
});

export const updateStructuredOperationsOffersBySearchTermSelector = selector({
  key: 'updateStructuredOperationsOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CoeOfferEntity[];
    return set(structuredOperationsBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsBySearchTermState);
    return item;
  },
});

export const structuredOperationsOffersQuery = selectorFamily<
  StructuredOperationsQueryData | null | undefined,
  { callEndpoint?: boolean }
>({
  key: 'NewApplication/structuredOperationsOffersQuery',
  get: ({ callEndpoint }) => {
    return async ({ get }) => {
      try {
        if (!callEndpoint) {
          return;
        }

        const structuredOperationsParams = get(structuredOperationsOffersParamsState);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.COES.V1.getOffersFilter({
          ...structuredOperationsParams,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = data?.map((item) => item.offer) || [];

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const structuredOperationsOffersBySearchTermQuery = selector({
  key: 'NewApplication/structuredOperationsOffersBySearchTermQuery',
  get: async ({ get }) => {
    const structuredOperationsParams = get(structuredOperationsOffersBySearchTermParamsState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    if (structuredOperationsParams.skipEndpointCall) {
      return;
    }

    const { data, meta } = await Service.COES.V1.getOffersFilter({
      ...structuredOperationsParams,
      screenName,
      advisorId: advisorSelected,
      partnerId: partnerSelected,
    });

    const normalizedData = data?.map((item) => item.offer) || [];

    return { data: normalizedData, meta };
  },
});

export const structuredOperationsOfferDetailsQuery = selector({
  key: 'NewApplication/structuredOperationsOfferDetailsQuery',
  get: async ({ get }) => {
    const selectedProduct = get(productItemState);
    const type = selectedProduct.current.type;
    const productId = selectedProduct?.current?.productId;
    const prevProductId = selectedProduct?.previous?.productId;

    if (!productId || type !== 'StructuredOperations' || productId === prevProductId) {
      const structuredOperationsDetails = get(structuredOperationsDetailsState);

      return { data: structuredOperationsDetails };
    }

    const response = await getOfferByDetailProductIdService({ productId });
    return response;
  },
});

export const hasStructuredOperationOptionBillingSelectedSelector = selector({
  key: 'hasStructuredOperationOptionBillingSelectedSelector',
  get: ({ get }) => {
    const product = get(structuredOperationsOptionBillingTypeSelectedState);
    return Object.keys(product)?.length > 0;
  },
});

export const updateStructuredOperationsDetailsSelector = selector({
  key: 'updateStructuredOperationsDetailsSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CoeOfferDetailEntity;
    return set(structuredOperationsDetailsState, data);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsDetailsState);
    return item;
  },
});

export const structuredOperationDetailsQuery = selectorFamily<
  { data: CoeOfferDetailEntity } | null | undefined,
  { productId: string; callEndpoint?: boolean }
>({
  key: 'NewApplication/structuredOperationDetailsQuery',
  get: ({ productId, callEndpoint }) => {
    return async () => {
      if (!callEndpoint) {
        return;
      }

      const { data } = await Service.COES.V1.getOffersByDetailProductId({
        productId,
        screenName,
      });

      const normalizedData = data?.offerDetail || {};

      return { data: normalizedData };
    };
  },
});

export const updateStructuredOperationProductSelectedSelector = selector({
  key: 'updateStructuredOperationProductSelectedSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CoeOfferEntity;
    return set(structuredOperationProductSelectedState, data);
  },
  get: ({ get }) => {
    const item = get(structuredOperationProductSelectedState);
    return item;
  },
});

export const structuredOperationsFilterOptionsQuery = selectorFamily<
  { data: CoeOfferStrategyDirectionEntity } | null | undefined,
  { skipEndpointCall?: boolean }
>({
  key: 'NewApplication/structuredOperationsFilterOptionsQuery',
  get: ({ skipEndpointCall }) => {
    return async ({ get }) => {
      try {
        if (skipEndpointCall) {
          const structuredOperationsOptionsFilter = get(structuredOperationsOptionsFilterState);
          return structuredOperationsOptionsFilter;
        }
        const { data: response } = await Service.COES.V1.getOffersStrategyDirection({
          screenName,
        });

        const data = response?.strategyDirectionFilter;

        return { data };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: {} as CoeOfferStrategyDirectionEntity };
        }

        throw error;
      }
    };
  },
});

export const updateStructuredOperationsOptionsFilterSelector = selector({
  key: 'updateStructuredOperationsOptionsFilterSelector',
  set: ({ set }, newValue) => {
    const value = { data: newValue } as { data: CoeOfferStrategyDirectionEntity };
    return set(structuredOperationsOptionsFilterState, value);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsOptionsFilterState);
    return item;
  },
});

export const updateStructuredOperationsMarketStatusSelector = selector({
  key: 'updateStructuredOperationsMarketStatusSelector',
  set: ({ set }, newValue) => {
    const value = newValue as ResponsesCoeMarketStatusResponse;
    return set(structuredOperationsMarketStatusState, value);
  },
  get: ({ get }) => {
    const item = get(structuredOperationsMarketStatusState);
    return item;
  },
});

export const coesMarketStatusQuery = selector<DynamicMarketStatusProps>({
  key: 'NewApplication/coesMarketStatusQuery',
  get: async () => {
    const { data } = await Service.COES.V1.getMarketStatus({
      screenName,
    });

    return {
      isOpen: data?.marketStatus.isOpened,
      message: data?.marketStatus.message || '',
      originalData: data,
    };
  },
});

export const coesOffersQueryUpdated = selectorFamily<
  {
    data: CoesOfferEntityNormalized[];
    meta: MetaResponse;
    filters?: DynamicCustomFiltersProps['filters'];
  },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/coesOffersQueryUpdated',
  get: ({ filtersToApply, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);

        const { data, meta } = await Service.COES.V1.getOffersFilter({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          sortItems: sortItems && [sortItems],
          page,
          limit: 12,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizeCoesOffers(data);
        const normalizedFilters = normalizeCoesFiltersFromMeta(meta);

        return { data: normalizedData, meta, filters: normalizedFilters.filters };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const coesCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'NewApplication/coesCustomFiltersQuery',
  get: () => {
    return async () => {
      const { data } = await Service.COES.V1.getOffersStrategyDirection({
        screenName,
      });

      const normalizedData = normalizeCoesFilters(data);

      return normalizedData;
    };
  },
});
